import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Page } from "@/components/PublicLayout";
import { SafetyMessage } from "@/components/SafetyMessage";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBlocks } from "@/hooks/useContent";

const EMPTY = { name: "", email: "", phone: "", contact_preference: "email", safe_to_leave_message: true, best_time: "", campus: "", topic: "counseling", message: "", acknowledged_not_monitored: false, website: "" };

const PHONE = "(909) 987-7110";
const linkPhone = (body) => {
  const [before, after = "."] = body.split(PHONE);
  return [before, <a key="tel" href="tel:9099877110" className="link">{PHONE}</a>, after];
};

const Field = ({ id, label, hint, children }) => (
  <div>
    <label htmlFor={id} className="label">{label}</label>
    {hint && <p className="help mb-1.5">{hint}</p>}
    {children}
  </div>
);

export default function ReachOutPage() {
  const { user } = useAuth();
  const { data: blocks } = useBlocks();
  const { data: opts } = useQuery({ queryKey: ["reach-out", "options"], queryFn: async () => (await api.get("/reach-out/options")).data, staleTime: 5 * 60_000 });
  const [f, setF] = useState(EMPTY);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(null);
  const [lines, setLines] = useState(null);
  useEffect(() => { if (user) setF((p) => ({ ...p, name: p.name || `${user.first_name} ${user.last_name}`.trim(), email: p.email || user.email })); }, [user]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const heading = blocks?.["reachout.heading"];
  const phone = blocks?.["reachout.phone"];
  const notMonitored = blocks?.["reachout.not_monitored"];
  const privacy = blocks?.["reachout.privacy"];
  const success = blocks?.["reachout.success"];
  const commitment = opts?.response_commitment || "within two business days";

  const submit = async (e) => {
    e.preventDefault();
    setPending(true);
    try {
      const { data } = await api.post("/reach-out", { ...f, email: f.email.trim() || null, phone: f.phone.trim() || null, best_time: f.best_time.trim() || null, campus: f.campus || null });
      setDone(data);
      if (data.crisis_lines) setLines(data.crisis_lines);
      window.scrollTo({ top: 0 });
    } catch (err) { toast.error(apiError(err)); } finally { setPending(false); }
  };

  return (
    <Page title={heading?.title || "Reach Out To Us"} lede={heading?.body} testId="reach-out-page">
      {lines && <SafetyMessage lines={lines} onDismiss={() => setLines(null)} />}
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          {done ? (
            <div className="surface confirm-pop p-7" role="status" data-testid="reach-out-success">
              <p className="font-serif text-2xl font-semibold">{success?.body || "Thank you! We'll be in touch shortly."}</p>
              <p className="mt-3">Your message reached our Care Director. We respond {commitment}, and only the way you asked us to.</p>
              {done.crisis_lines && <p className="mt-3 font-semibold text-terracotta-600" data-testid="reach-out-urgent-note">Because of what you shared, your message was marked urgent and our responders were alerted. You do not have to wait for us: call or text 988 any time.</p>}
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/resources" className="btn-secondary" data-testid="reach-out-resources-link">Browse Resources</Link>
                <Link to="/crisis" className="btn-quiet">Crisis & Urgent Help</Link>
                {!user && <Link to="/sign-up" className="btn-quiet">Create A Free Account</Link>}
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" noValidate data-testid="reach-out-form">
              <Field id="ro-name" label="Your name"><input id="ro-name" className="input" value={f.name} onChange={set("name")} required maxLength={120} autoComplete="name" data-testid="reach-out-name" /></Field>
              <div className="grid gap-6 sm:grid-cols-2">
                <Field id="ro-email" label="Email"><input id="ro-email" type="email" className="input" value={f.email} onChange={set("email")} autoComplete="email" data-testid="reach-out-email" /></Field>
                <Field id="ro-phone" label="Phone"><input id="ro-phone" type="tel" className="input" value={f.phone} onChange={set("phone")} placeholder="(909) 555-0142" autoComplete="tel" data-testid="reach-out-phone" /></Field>
              </div>
              <p className="help -mt-3">Give us at least one way to reach you.</p>
              <fieldset>
                <legend className="label">Best way to reach you</legend>
                <div className="mt-1 flex flex-wrap gap-2" data-testid="reach-out-preference">
                  {[["email", "Email me"], ["call", "Call me"], ["text", "Text me"]].map(([v, l]) => (
                    <label key={v} className={`tap cursor-pointer border px-4 ${f.contact_preference === v ? "border-olive-900 bg-olive-900 text-cream-50" : "border-sage-400 bg-cream-50"}`}>
                      <input type="radio" name="contact_preference" value={v} checked={f.contact_preference === v} onChange={set("contact_preference")} className="sr-only" data-testid={`reach-out-preference-${v}`} />{l}
                    </label>
                  ))}
                </div>
              </fieldset>
              <label className="tap justify-start gap-3 cursor-pointer border border-sage-400/70 bg-cream-100 px-4" data-testid="reach-out-safe-label">
                <input type="checkbox" className="h-5 w-5 accent-[#212B23]" checked={f.safe_to_leave_message} onChange={set("safe_to_leave_message")} data-testid="reach-out-safe" />
                <span>It is safe to leave me a voicemail, text, or email</span>
              </label>
              {!f.safe_to_leave_message && <p className="-mt-3 text-sm text-ink-900" data-testid="reach-out-safe-note">Understood. We will only reach you the way you chose above, and we will not leave a message if you do not answer.</p>}
              <Field id="ro-time" label="Best time to reach you (optional)"><input id="ro-time" className="input" value={f.best_time} onChange={set("best_time")} placeholder="Weekday mornings, after 6 PM…" maxLength={120} data-testid="reach-out-best-time" /></Field>
              <div className="grid gap-6 sm:grid-cols-2">
                <Field id="ro-campus" label="Your campus (optional)">
                  <select id="ro-campus" className="input" value={f.campus} onChange={set("campus")} data-testid="reach-out-campus">
                    <option value="">Choose a campus</option>
                    {(opts?.campuses || []).map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field id="ro-topic" label="What do you need?">
                  <select id="ro-topic" className="input" value={f.topic} onChange={set("topic")} data-testid="reach-out-topic">
                    {(opts?.topics || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </Field>
              </div>
              <Field id="ro-message" label="Your message" hint="A few sentences is plenty. You do not need to share anything you are not ready to.">
                <textarea id="ro-message" className="input min-h-[160px]" value={f.message} onChange={set("message")} required minLength={10} maxLength={3000} data-testid="reach-out-message" />
                <p className="help mt-1 text-right">{`${f.message.length}/3000`}</p>
              </Field>
              <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
                <label htmlFor="ro-website">Leave this field empty</label>
                <input id="ro-website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} />
              </div>
              <label className="tap justify-start gap-3 cursor-pointer" data-testid="reach-out-ack-label">
                <input type="checkbox" className="h-5 w-5 accent-[#212B23]" checked={f.acknowledged_not_monitored} onChange={set("acknowledged_not_monitored")} required data-testid="reach-out-ack" />
                <span>I understand this form is not monitored around the clock. If I am in crisis I will call or text 988, or call 911.</span>
              </label>
              <button type="submit" className="btn-primary" disabled={pending || !f.acknowledged_not_monitored || f.message.trim().length < 10 || !f.name.trim()} data-testid="reach-out-submit">{pending ? "Sending…" : "Send To Our Care Team"}</button>
            </form>
          )}
        </div>
        <aside className="lg:col-span-5 space-y-6">
          {privacy && <div className="surface p-6" data-testid="reach-out-privacy"><h2 className="text-xl">{privacy.title}</h2><p className="mt-2 text-sm text-ink-900">{privacy.body}</p></div>}
          <p className="text-ink-500" data-testid="reach-out-commitment">We respond {commitment}.</p>
          {phone && <p className="text-lg" data-testid="reach-out-phone-note">{linkPhone(phone.body)}</p>}
          {notMonitored && <p className="text-ink-500" data-testid="reach-out-not-monitored">{notMonitored.body}</p>}
        </aside>
      </div>
    </Page>
  );
}
